import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { bmoApi } from '../services/api';
import { colors, getScoreColor, KAYAKALP_PASS_SCORE } from '../theme';
import { Card, StatCard, ProgressBar, SectionHeader, Spinner, AlertBanner, EmptyState } from '../components/common';

export default function ScoresPage() {
  const navigate = useNavigate();
  const [facilities, setFacilities] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [filter,     setFilter]     = useState('all'); // all | pass | fail

  useEffect(() => {
    bmoApi.getFacilityList()
      .then((d) => setFacilities(d?.facilities || []))
      .catch((e) => setError(e?.message || 'Could not load scores.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner size={48} />;
  if (error)   return <AlertBanner type="error" message={error} />;

  const passing  = facilities.filter((f) => (f.score_percentage ?? 0) >= KAYAKALP_PASS_SCORE);
  const failing  = facilities.filter((f) => (f.score_percentage ?? 0) < KAYAKALP_PASS_SCORE);
  const avgScore = facilities.length
    ? facilities.reduce((s, f) => s + (f.score_percentage ?? 0), 0) / facilities.length : 0;

  const displayed = filter === 'pass' ? passing : filter === 'fail' ? failing : [...facilities].sort((a, b) => (b.score_percentage ?? 0) - (a.score_percentage ?? 0));

  const chartData = displayed.map((f) => ({
    name:  f.name.length > 16 ? f.name.slice(0, 16) + '…' : f.name,
    score: Math.round(f.score_percentage ?? 0),
    color: getScoreColor(f.score_percentage ?? 0),
  }));

  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <h1 style={{ margin:'0 0 4px', fontSize:24, fontWeight:700, color: colors.text.primary }}>🏆 Score Overview</h1>
        <p style={{ margin:0, fontSize:14, color: colors.text.secondary }}>District score ranking — Kayakalp 2024</p>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap:16, marginBottom:24 }}>
        <StatCard icon="📊" label="District Average" value={`${Math.round(avgScore)}%`} color={getScoreColor(avgScore)} />
        <StatCard icon="✅" label="Passing (≥70%)"   value={passing.length} color={colors.success[700]} bg={colors.success[50]} />
        <StatCard icon="❌" label="Failing (<70%)"    value={failing.length} color={colors.error[700]}   bg={colors.error[50]}   />
        <StatCard icon="🏥" label="Total Facilities"  value={facilities.length} color={colors.primary[700]} />
      </div>

      {/* Pass mark reference */}
      <div style={{ background: colors.warning[50], border:`1px solid ${colors.warning[200]}`, borderRadius:8, padding:'10px 16px', marginBottom:16, display:'flex', alignItems:'center', gap:8 }}>
        <span style={{ color: colors.warning[700], fontWeight:600, fontSize:13 }}>Pass mark: {KAYAKALP_PASS_SCORE}% — facilities below this mark do not qualify for Kayakalp award</span>
      </div>

      {/* Bar chart */}
      <Card style={{ marginBottom:16 }}>
        <SectionHeader title="Score Ranking" subtitle={`${displayed.length} facilities`} />
        {displayed.length === 0
          ? <EmptyState icon="📊" title="No data" />
          : (
            <ResponsiveContainer width="100%" height={Math.max(200, displayed.length * 36)}>
              <BarChart data={chartData} layout="vertical" margin={{ left: 8 }}>
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize:12 }} tickFormatter={(v) => `${v}%`} />
                <YAxis dataKey="name" type="category" width={160} tick={{ fontSize:12 }} />
                <Tooltip formatter={(v) => [`${v}%`, 'Score']} />
                <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                  {chartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )
        }
      </Card>

      {/* Filter + table */}
      <Card style={{ padding:0 }}>
        <div style={{ padding:'14px 20px', borderBottom:`1px solid ${colors.divider}`, display:'flex', gap:8 }}>
          {[['all','All'],['pass','Passing'],['fail','Failing']].map(([key, lbl]) => (
            <button key={key} onClick={() => setFilter(key)}
              style={{ padding:'5px 14px', borderRadius:99, border:'none', cursor:'pointer', fontSize:13, fontWeight:500, background: filter === key ? colors.primary[700] : colors.neutral[100], color: filter === key ? colors.white : colors.text.secondary }}>
              {lbl}
            </button>
          ))}
        </div>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background: colors.neutral[50] }}>
                {['#','Facility','Type','Score','Status'].map((h) => (
                  <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:12, fontWeight:600, color: colors.text.secondary, textTransform:'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayed.map((f, i) => {
                const pct     = f.score_percentage ?? 0;
                const passing = pct >= KAYAKALP_PASS_SCORE;
                return (
                  <tr key={f.id} onClick={() => navigate(`/facilities/${f.id}`)} style={{ cursor:'pointer', borderBottom:`1px solid ${colors.neutral[100]}` }}>
                    <td style={{ padding:'12px 16px', fontSize:13, color: colors.text.secondary }}>{i + 1}</td>
                    <td style={{ padding:'12px 16px', fontSize:14, fontWeight:500, color: colors.text.primary }}>{f.name}</td>
                    <td style={{ padding:'12px 16px', fontSize:13, color: colors.text.secondary }}>{f.type_label}</td>
                    <td style={{ padding:'12px 16px', minWidth:160 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <ProgressBar pct={pct} />
                        <span style={{ fontSize:14, fontWeight:700, color: getScoreColor(pct), minWidth:40 }}>{Math.round(pct)}%</span>
                      </div>
                    </td>
                    <td style={{ padding:'12px 16px' }}>
                      <span style={{ fontSize:11, fontWeight:700, padding:'3px 8px', borderRadius:99, background: passing ? colors.success[50] : colors.error[50], color: passing ? colors.success[700] : colors.error[700] }}>
                        {passing ? '✓ PASS' : '✗ FAIL'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
